import BgGlassmorphism from '@/components/BgGlassmorphism/BgGlassmorphism'
import Box from '@/components/Box'
import Heading from '@/components/Heading/Heading'
import StayDatesRangeInput from "@/components/HeroSearchForm/StayDatesRangeInput"
import { DateRage } from '@/components/HeroSearchForm/StaySearchForm'
import PriceSummary from '@/components/PriceSummary'
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@/components/ui/table"
import { GSTFactor } from '@/contains/contants'
import { useAppContext } from '@/context/AppContext'
import { useCartContext } from "@/context/CartContext"
import { useFirebaseContext } from "@/context/FirebaseContext"
import { PaymentStatus } from "@/generated/zeus"
import { apiFetch } from '@/hooks/fetch'
import useUserContext from '@/hooks/useUserContext'
import { mutation, query } from "@/hooks/useZeus"
import { fireEvent, fireMatomoEvent, formatPrice, titleToUrl } from "@/lib/utils"
import Loader from '@/shared/FullScreenLoader/Loading'
import Input from '@/shared/Input/Input'
import NcImage from "@/shared/NcImage/NcImage"
import Select from '@/shared/Select/Select'
import Textarea from '@/shared/Textarea/Textarea'
import { TrashIcon } from '@heroicons/react/24/solid'
import { useUser } from '@stackframe/react'
import moment from "moment"
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useForm } from "react-hook-form"
import { Link, useNavigate, useSearchParams } from "react-router-dom"
import { toast } from "react-toastify"
import { AssetFile } from "../CheckOutPage/types"
import CreativesBox from "./CreativesBox"
import { AdboardFragment } from "./types"
import LoadingButton from '@/components/LoadingButton'

const adPurposes = {
  BRAND_AWARENESS: "Brand awareness",
  PRODUCT_LAUNCH: "Product launch",
  PROMOTION: "Promotion",
  SALES_AND_OFFERS: "Sales and offers",
  SERVICE_ANNOUNCEMENT: "Service announcement",
  OTHER: "Other",
}

type CampaignForm = {
  name: string
  adPurpose: keyof typeof adPurposes,
  description: string
  businessCategoryId: string,
  user: {
    name: string
    email: string
    phoneNumber?: string | null
  }
}

export default function CampaignNew({
  className,
}: {
  className?: string
}) {
  const [searchParams] = useSearchParams();
  const { adBoards: cartAdboards } = useCartContext();
  const { loading: authLoading, user: fbUser } = useFirebaseContext()
  // const [currentBox, setCurrentBox] = useState<number>(2);
  useUser({ or: 'redirect' })
  const { user } = useUserContext()
  const { register, handleSubmit, formState, setValue, getValues, } = useForm<CampaignForm>()
  const navigate = useNavigate()
  const { businessCategories } = useAppContext()
  const [creatives, setCreatives] = useState<AssetFile[]>([])
  const [selectedDate, setSelectedDate] = useState<DateRage>({
    startDate: null,
    endDate: null,
  })

  const [data, setAdboards] = useState<{
    adBoards: AdboardFragment[]
  }>({
    adBoards: []
  });

  const loading = !data.adBoards.length

  useEffect(() => {

    const adBoardId = searchParams.get('adboardId')
    let adBoardsIds = [adBoardId]
    if (!adBoardId) {
      adBoardsIds =  cartAdboards.map(item => item.id)
    } 

    if(!adBoardsIds.length){
      return;
    }

    apiFetch('/api/adboard/adBoardList', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        adBoardsIds
      })
    }).then(res => res.json())
      .then(res => {
        setAdboards({
          adBoards: res.adBoards
        })
      }).catch(e => {
        console.error('Failed to fetch adboards', e);
        toast.error('Failed to fetch adboards, Please try again later');
      })
  }, [cartAdboards])

  const [selectedAdboards, setSelectedAdboards] = useState<AdboardFragment[]>([])

  useEffect(() => {
    if (user) {
      setValue('user.name', user.displayName)
      setValue('user.email', user.email)
      setValue('user.phoneNumber', user.phoneNumber)
    }
  }, [user])

  useEffect(() => {

    if (!data?.adBoards.length) {
      return;
    }

    // const adBoardId = searchParams.get('adboardId')
    const startDate = searchParams.get('startDate')
    const endDate = searchParams.get('endDate')

    setSelectedAdboards(data.adBoards)

    setSelectedDate({
      startDate: startDate ? moment(startDate) : null,
      endDate: endDate ? moment(endDate) : null
    })

  }, [data, cartAdboards, searchParams])

  const [priceSummary, setPriceSummary] = useState<{
    days: number,
    subtotal: number,
    pricePerDay: number,
    gst: number,
    totalRent: number
  }>({
    days: 0,
    subtotal: 0,
    pricePerDay: 0,
    gst: 0,
    totalRent: 0
  })

  useEffect(() => {

    if (!selectedDate.startDate || !selectedDate.endDate) {
      return
    }

    const days = selectedDate.endDate?.diff(selectedDate.startDate, 'days') + 1;

    const totalPricePerDay = Object.values(selectedAdboards).reduce((acc: any, item) => {
      return acc + item.pricePerDay;
    }, 0);
    setPriceSummary({
      days: days,
      pricePerDay: totalPricePerDay,
      subtotal: totalPricePerDay * days,
      gst: Number(((totalPricePerDay * days) * GSTFactor).toFixed(0)),
      totalRent: Number(((totalPricePerDay * days) * 1.18).toFixed(0))
    })

  }, [selectedAdboards, selectedDate])

  async function handleCreateCampaign() {
    const formData = getValues()
    const userId = (await query({
      loggedInUser: {
        id: true
      }
    })).loggedInUser?.id

    if (!userId || !user) {
      toast.error('Please login to continue');
      return null;
    }
    if (Object.keys(selectedAdboards).length === 0) {
      toast.error('Please select at least one adboard');
      return
    }

    if (!selectedDate.endDate || !selectedDate.startDate) {
      toast.error('Please select a valid date range');
      return
    }

    if (creatives.length === 0) {
      toast.error('Please upload at least one creative');
      return
    }

    const assetIds = await Promise.all(creatives?.map(async item => {
      return item.id
    }));

    fireEvent('begin_checkout', {
      ecommerce: {
        currency: "INR",
        value: priceSummary.totalRent,
        items: Object.values(selectedAdboards).map((item, i) => ({
          item_id: item.id,
          item_name: item.title,
          index: i,
          location_id: item.city,
          price: item.pricePerDay,
          quantity: 1
        }))
      }
    })

    if(!formData.user.phoneNumber){
      toast.error('Please enter your phone number');
      return
    }

    const { createOneCampaignBooking } = await mutation({
      createOneCampaignBooking: [{
        data: {
          purpose: formData.adPurpose,
          assetIds: assetIds,
          name: formData.name,
          businessCategoryId: formData.businessCategoryId,
          bookingDetails: {
            email: user.email,
            name: user.displayName,
            phone: formData.user.phoneNumber
          },
          priceSummary: {
            discount:0,
            days: priceSummary.days, 
            subtotal: priceSummary.subtotal,
            // payableRent: priceSummary.subtotal,
            rentPerDay: priceSummary.pricePerDay,
            serviceCharge: Number(priceSummary.gst.toFixed(0)),
            totalRent: Number(priceSummary.totalRent.toFixed(0))
          },
          paymentStatus: PaymentStatus.PENDING,
          origin: window.location.origin,
          selectedDates: {
            startDate: selectedDate.startDate.toISOString(),
            endDate: selectedDate.endDate.toISOString()
          },
          userEmail : user.email,
          adBoardIds: selectedAdboards.map(item => item.id),
          // bookings: {
          //   createMany: {
          //     data: Object.values(selectedAdboards).map(item => {
          //       const subtotal = item.pricePerDay * priceSummary.days;
          //       const gst = Number((subtotal * GSTFactor).toFixed(0));
          //       const totalRent = subtotal + gst;
          //       return ({
          //         additionalInformation: formData.description,
          //         origin: window.location.origin,
          //         isPaid: false,
          //         purpose: formData.adPurpose,
          //         priceSummary: {
          //           days: priceSummary.days,
          //           subtotal: subtotal,
          //           payableRent: subtotal,
          //           rentPerDay: item.pricePerDay,
          //           serviceCharge: gst,
          //           totalRent: totalRent
          //         },
          //         businessCategoryId: formData.businessCategoryId,
          //         workspaceId: item.workspaceId,
          //         adBoardId: item.id,
          //         bookingCode: Math.random().toString(36).substring(7), //Should be removed
          //         bookingDetails: {
          //           email: user.email,
          //           name: user.displayName,
          //           phone: formData.user.phoneNumber
          //         },
          //         deploymentStatus: DeploymentStatus.NOT_STARTED,
          //         paymentStatus: PaymentStatus.PENDING,
          //         bookingStatus: BookingStatus.ENQUIRY,
          //         selectedDates: {
          //           startDate: selectedDate.startDate,
          //           endDate: selectedDate.endDate
          //         },
          //         isEnquiry: true,
          //         enquiryDate: new Date(),
          //         userId: userId,
          //       })
          //     })
          //   }
          // }
        }
      }, {
        id: true
      }]
    }).catch(e => {
      toast.error('Failed to create campaign, Please try again later');
      throw e;
    })

    fireEvent('campaign_creation_completed', {
      currency: "INR",
      value: priceSummary.totalRent,
      items: Object.values(selectedAdboards).map(item => ({
        id: item.id,
        name: item.title,
        quantity: priceSummary.days,
        price: item.pricePerDay
      }))
    });

    const days = priceSummary?.days;
    Object.values(selectedAdboards).forEach(adBoard => {
      fireMatomoEvent(['addEcommerceItem',
        adBoard.id,// product.productSKU,
        adBoard.title,// product.productName,
        adBoard.city,// product.productCategory,
        adBoard.pricePerDay,// product.price,
        days// product.quantity
      ])
    })

    fireMatomoEvent(['trackEcommerceCartUpdate', priceSummary?.subtotal])

    toast.success('Campaign created successfully.')
    navigate('/pay-done-campaign/' + createOneCampaignBooking.id + window.location.search)

  }

  function renderSidebars() {
    return (
      <div className='col-span-5 sticky top-24 flex flex-col gap-4'>
        <Box title={<>
          Adboards
          {selectedAdboards.length > 0 && <span className='text-xs text-neutral-500 dark:text-neutral-400'> {(selectedAdboards).length} adboards selected</span>}
        </>} classname='flex flex-col gap-2'>
          <div className='max-h-60 overflow-auto relative'>
            
            {loading && <div className='p-8'> <Loader>
              Loading adboards details...
            </Loader></div>}
            <Table className=''>
              <TableBody className=''>
                {selectedAdboards.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell colSpan={4}>
                      <div>
                        <div className="flex flex-col lg:flex-row items-center gap-4">
                          <NcImage src={item.featuredImage + '-webimage'} className="w-full lg:w-20 object-cover rounded-lg" />
                          <div className="flex flex-row flex-1 w-full">
                            <div className="flex-1 flex flex-col justify-between ">
                              <Link target="_blank" to={`/hoardings/${titleToUrl(item.title)}/${item.id}`} className="flex items-center gap-2">
                                {item.title}
                              </Link>
                              <div className="flex gap-2 flex-col">

                                <div className="flex items-center gap-1 text-xs">
                                  <span>{item.city} </span>
                                  <span>|</span>
                                  <span>{formatPrice(item.pricePerDay)}/day</span>
                                </div>

                              </div>
                            </div>

                            <TrashIcon className="cursor-pointer text-black" width={'20'} onClick={() => {
                              setSelectedAdboards(selectedAdboards.filter(adboard => adboard.id != item.id));
                            }} />
                          </div>
                        </div>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="">
            <form className="flex flex-col border border-neutral-200 dark:border-neutral-700 rounded-xl lg:rounded-full ">
              <StayDatesRangeInput
                // slotMap={slotMap}
                // adsPerLoop={data.adBoard?.adsPerLoop||1}
                // blockedDates={blockedDates}
                wrapClassName="divide-x divide-neutral-200 dark:divide-neutral-700 !grid-cols-1 sm:!grid-cols-2"
                onChange={(date) => setSelectedDate(date)}
                fieldClassName="p-3"
                defaultValue={selectedDate}
                anchorDirection={"right"}
                className="flex-1"
              />
            </form>
          </div>

          {/* <div className='p-4 mt-4 border rounded-full'>
            {selectedDate &&
          <StayDatesRangeInput
            onChange={(date)=>{
              setSelectedDate(date);
            }}
            defaultValue={{
              startDate: selectedDate.startDate,
              endDate: selectedDate.endDate,
            }}/>
            }
          </div> */}

          <div className='mt-4'>
            <PriceSummary priceSummary={priceSummary}></PriceSummary>
          </div>
        </Box>

      </div>
    )
  }

  return (
    <div
      className={`nc-ListingFlightsPage relative ${className}`}
      data-nc-id="ListingFlightsPage"
    >

      {/* {loading && <Loader fullScreen/>} */}
      <Helmet>
        <title>Campaigns | Adboard Booking</title>
      </Helmet>
      <BgGlassmorphism />

      <div className="container relative py-4">
        <div className='flex justify-between items-center' >
          <Heading desc={user?.email ? "lets start by filling the campaign details" : 'lets start by logging to your account first'}>Create a new enquiry</Heading>
        </div>

        {!fbUser?.email ? <div className="py-48">{authLoading ? <Loader /> : <></>}</div> :
          <div>
            <div className='grid lg:grid-cols-12 grid-cols-1 gap-4 relative'>
              <div className='flex flex-col gap-4 col-span-7 h-full lg:px-2'>
                <div className='lg:hidden'>
                  {renderSidebars()}

                </div>
                <Box title={<>
                  Campaign details
                  {(formState.errors.name || formState.errors.adPurpose || formState.errors.businessCategoryId) && <span className='text-xs text-red-500 dark:text-error-400'> * Missing</span>}
                </>}>
                  <div className='flex flex-col gap-2'>
                    <Input label="Campaign name" type="text" {...register('name', { required: true })} aria-invalid={formState.errors.name ? "true" : "false"} />
                    {formState.errors.name && <div className='text-red-500 text-sm'>Campaign name is required</div>}
                    <Textarea label='Brief detail about your campaign' {...register('description')} />
                    <Select label="Business category" placeholder='Select business category' {...register('businessCategoryId', { required: true })}>
                      <option value="">Select business category</option>
                      {businessCategories.map((item) => (
                        <option value={item.id}>{item.name}</option>
                      ))}
                    </Select>
                    {formState.errors.businessCategoryId && <div className='text-red-500 text-sm'>Ad category is required</div>}
                    <Select label="Advertisement purpose" placeholder='Select category' {...register('adPurpose', { required: true })}>
                      <option value="">Select purpose</option>
                      {Object.keys(adPurposes).map((item) => (
                        <option value={item}>{adPurposes[item]}</option>
                      ))}
                    </Select>
                    {formState.errors.adPurpose && <div className='text-red-500 text-sm'>Ad purpose is required</div>}
                  </div>

                </Box>

                <Box title={<>
                  User details
                  {(formState.errors.user?.phoneNumber) && <span className='text-xs text-red-500 dark:text-error-400'> * Missing</span>}
                </>} >
                  <div className='flex flex-col gap-2'>
                    <Input type="text" label="Name" placeholder='Name' {...register('user.name', { required: true })} aria-invalid={formState.errors.name ? "true" : "false"} />
                    {formState.errors.user?.name && <div className='text-red-500 text-sm'>Name is required</div>}
                    <Input label="Email" type="email" placeholder='Email' {...register('user.email', {
                      required: true,
                      pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
                    })} aria-invalid={formState.errors.name ? "true" : "false"} />
                    {formState.errors.user?.email && <div className='text-red-500 text-sm'>Email is required</div>}
                    <Input label="Phone number" type="text"
                      {...register('user.phoneNumber', {
                        required: true,
                        pattern: /^(\+91[\-\s]?)?[6-9]\d{9}$/
                      })
                      } />
                    {formState.errors.user?.phoneNumber && <div className='text-red-500 text-sm'>Valid phone number is required</div>}
                  </div>

                </Box>

                <Box title={<>
                  Creatives
                  {(creatives.length == 0 && formState.dirtyFields.name) && <span className='text-xs text-red-500 dark:text-error-400'> * Missing</span>}
                </>}>
                  <CreativesBox selectedAdboards={Object.values(selectedAdboards)} creatives={creatives} setCreatives={setCreatives} />
                </Box>

                {/* <div className='mb-2'></div> */}
              </div>
              <div className='col-span-5 hidden lg:flex flex-col gap-4'>
                {renderSidebars()}
              </div>

            </div>
            <div className='p-4 flex flex-col justify-end gap-4 mt-2'>
              <div>
                <p className='text-xs mt-4'>
                  By selecting the button below, I agree to the <a className='text-blue-800' href="https://www.adboardbooking.com/cancellation.html" target="_blank">Adboard Booking Rules, and Refund Policy</a> and that Adboard Booking can charge my payment method if I’m responsible for damage. I agree to pay the total amount shown if the Host accepts my booking request.
                </p>
              </div>
              <div className=''>
                <b>
                  You will not be charged until your booking is approved.
                </b>
              </div>
              <div className="flex gap-2 items-end">
                <LoadingButton onClick={handleSubmit(handleCreateCampaign)} className="whitespace-nowrap">Review & Submit</LoadingButton>
                {/* <DialogBody isOpen={openCampaignSummary} 
                      title="Campaign summary"
                      setOpen={setOpenCampaignSummary}>
                      <CampaignSummary 
                        selectedAdboards={Object.values(selectedAdboards)} 
                        selectedDate={selectedDate} 
                        priceSummary={priceSummary} 
                        creatives={creatives}
                        onClick={}
                      />
                    </DialogBody>  */}
              </div>
              <span className="text-xs">
                Clicking above button will create a campaign and you will be redirected to the campaign page. Upon approval of the campaign, you will be charged the total amount shown above.
              </span>
            </div>
          </div>
        }
      </div>
    </div>

  )
}
